import React, { useEffect, useState, useMemo } from "react";
import { articlesAPI } from "../services/api";
import "./Content.css";
import "../components/Form.css";
import Button from "../components/Button";
import { useToasts } from "../hooks/useToasts";
import Spinner from "../components/Spinner";
import Filter from "../components/Filter";

const isEffectivelyDeactivated = (article) =>
  Boolean(
    article?.effectiveIsDeactivatedByAdmin ?? article?.isDeactivatedByAdmin,
  );

const AdminContent = () => {
  const { addToast } = useToasts();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatorFilter, setCreatorFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [quickDate, setQuickDate] = useState("");
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  useEffect(() => {
    async function fetchUserAndArticles() {
      try {
        setLoading(true);
        setError(null);

        const res = await articlesAPI.getAllArticles();
        setArticles(res.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchUserAndArticles();
  }, []);

  const creatorOptions = useMemo(() => {
    const ids = articles
      .map((a) => a.creatorId?._id || a.creatorId)
      .filter(Boolean);
    return Array.from(new Set(ids));
  }, [articles]);

  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      let match = true;
      if (
        creatorFilter &&
        (article.creatorId?._id || article.creatorId) !== creatorFilter
      )
        match = false;
      if (article.createdAt) {
        const artDate = new Date(article.createdAt);
        if (quickDate) {
          const now = new Date();
          console.log("Quick date filter:", quickDate);
          console.log("Note date:", artDate);
          console.log("Now:", now);

          if (quickDate === "today") {
            const isToday =
              artDate.getFullYear() === now.getFullYear() &&
              artDate.getMonth() === now.getMonth() &&
              artDate.getDate() === now.getDate();
            console.log("Is today?", isToday);
            match = match && isToday;
          } else if (quickDate === "week") {
            // Last 7 days instead of ISO week
            const sevenDaysAgo = new Date(now);
            sevenDaysAgo.setDate(now.getDate() - 7);
            console.log("Seven days ago:", sevenDaysAgo);
            match = match && artDate >= sevenDaysAgo;
          } else if (quickDate === "month") {
            match =
              match &&
              artDate.getFullYear() === now.getFullYear() &&
              artDate.getMonth() === now.getMonth();
          }
        } else if (dateFilter) {
          const artDateStr = artDate.toISOString().slice(0, 10);
          if (artDateStr !== dateFilter) match = false;
        }
      }
      return match;
    });
  }, [articles, creatorFilter, dateFilter, quickDate]);

  async function handleStatusToggle(articleId) {
    try {
      const response = await articlesAPI.toggleArticleStatus(articleId);

      setArticles((prev) =>
        prev.map((article) =>
          article._id === articleId
            ? {
                ...article,
                isDeactivatedByAdmin:
                  response.data.article.isDeactivatedByAdmin,
                effectiveIsDeactivatedByAdmin:
                  response.data.article.isDeactivatedByAdmin,
              }
            : article.lessonId &&
                String(
                  typeof article.lessonId === "object"
                    ? article.lessonId._id || article.lessonId
                    : article.lessonId,
                ) === articleId
              ? {
                  ...article,
                  inheritedDeactivatedByAdmin:
                    response.data.article.isDeactivatedByAdmin,
                  effectiveIsDeactivatedByAdmin: Boolean(
                    article.isDeactivatedByAdmin ||
                      response.data.article.isDeactivatedByAdmin,
                  ),
                }
              : article
        )
      );

      const statusMessage = response.data.article.isDeactivatedByAdmin
        ? "Note deactivated"
        : "Note reactivated";
      addToast(statusMessage, "success-info");
    } catch (error) {
      const errorData = error.response?.data;
      addToast(errorData?.error || "Could not toggle article status", "error-warning");
    }
  }

  return (
    <div className="form-content-frame table-page">
      <div className="content-header">
        <h1>Content</h1>
        {(creatorFilter || dateFilter || quickDate) && (
          <Button
            className={`button-reset${
              creatorFilter || dateFilter || quickDate
                ? " button-reset-visible"
                : ""
            }`}
            onClick={() => {
              setCreatorFilter("");
              setDateFilter("");
              setQuickDate("");
            }}
          >
            Reset Filters
          </Button>
        )}
      </div>

      <div className="content-filters-section">
        <div className="content-filter-group">
          <Filter
            value={creatorFilter}
            onChange={(e) => setCreatorFilter(e.target.value)}
          >
            <option value="">All Creators</option>
            {creatorOptions.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </Filter>
        </div>

        <div className="content-filter-group content-time-filters">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setQuickDate("");
            }}
          />
          <button
            type="button"
            className={`content-btn-filter ${
              quickDate === "today" ? "active" : ""
            }`}
            onClick={() => {
              setQuickDate("today");
              setDateFilter("");
            }}
          >
            Today
          </button>
          <button
            type="button"
            className={`content-btn-filter ${
              quickDate === "week" ? "active" : ""
            }`}
            onClick={() => {
              setQuickDate("week");
              setDateFilter("");
            }}
          >
            Week
          </button>
          <button
            type="button"
            className={`content-btn-filter ${
              quickDate === "month" ? "active" : ""
            }`}
            onClick={() => {
              setQuickDate("month");
              setDateFilter("");
            }}
          >
            Month
          </button>
        </div>
      </div>

      <Spinner visible={loading} />
      {error && <div className="content-error">Error: {error}</div>}

      <div className="content-table-container">
        <table className="content-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Title</th>
              <th>Author</th>
              <th>Lesson</th>
              <th>Body</th>
              <th>Creator</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filteredArticles.length === 0 ? (
              <tr>
                <td colSpan="8" className="content-empty-message">
                  No articles found
                </td>
              </tr>
            ) : (
              filteredArticles.map((article, index) => (
                <React.Fragment key={article._id}>
                  <tr>
                    <td>{index + 1}</td>
                    <td>{article.title}</td>
                    <td>{article.author}</td>
                    <td>{article.chapter || "-"}</td>
                    <td>
                      <div 
                        className="body-preview"
                        onClick={() => {
                          const modal = document.createElement('div');
                          modal.className = 'body-modal';
                          modal.innerHTML = `
                            <div class="body-modal-content">
                              <span class="body-modal-close">&times;</span>
                              <div class="body-modal-text">${article.body.replace(/\n/g, '<br>')}</div>
                            </div>
                          `;
                          document.body.appendChild(modal);
                          
                          const closeModal = () => {
                            document.body.removeChild(modal);
                            document.removeEventListener('keydown', handleEsc);
                          };
                          
                          const handleEsc = (e) => {
                            if (e.key === 'Escape') closeModal();
                          };
                          
                          document.addEventListener('keydown', handleEsc);
                          
                          modal.querySelector('.body-modal-close').onclick = closeModal;
                          modal.onclick = (e) => {
                            if (e.target === modal) closeModal();
                          };
                        }}
                      >
                        {article.body.length > 100 
                          ? `${article.body.substring(0, 100)}...` 
                          : article.body
                        }
                      </div>
                    </td>
                    <td>
                      <div>{article.creatorId?._id || article.creatorId}</div>
                      <div>{article.creatorEmail}</div>
                    </td>
                    <td>
                      {article.createdAt
                        ? new Date(article.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="content-actions-cell">
                      <Button
                        className={
                          isEffectivelyDeactivated(article)
                            ? "button-btn-light-green"
                            : "button-btn-light-red"
                        }
                        disabled={
                          article.inheritedDeactivatedByAdmin &&
                          !article.isDeactivatedByAdmin
                        }
                        onClick={() => handleStatusToggle(article._id)}
                      >
                        {article.inheritedDeactivatedByAdmin &&
                        !article.isDeactivatedByAdmin
                          ? "Deactivated"
                          : isEffectivelyDeactivated(article)
                          ? "Reactivate"
                          : "Deactivate"}
                      </Button>

                      {article.history && article.history.length > 0 && (
                        <Button
                          className="button-btn-light-gray"
                          onClick={() => {
                            setExpandedHistoryId(
                              expandedHistoryId === article._id
                                ? null
                                : article._id
                            );
                          }}
                        >
                          History ({article.history.length})
                        </Button>
                      )}
                    </td>
                  </tr>
                  {expandedHistoryId === article._id && (
                    <tr>
                      <td colSpan="8" className="content-history-cell">
                        <div className="content-history-section">
                          <div className="content-history-title">Edit History</div>
                          {article.history
                            .slice()
                            .reverse()
                            .map((historyItem, idx) => (
                              <div key={idx} className="content-history-item">
                                {historyItem.oldTitle !== null && (
                                  <div>Title: {historyItem.oldTitle}</div>
                                )}
                                {historyItem.oldAuthor !== null && (
                                  <div>Author: {historyItem.oldAuthor}</div>
                                )}
                                {historyItem.oldChapter !== null && (
                                  <div>Lesson: {historyItem.oldChapter}</div>
                                )}
                                {historyItem.oldBody !== null && (
                                  <div>Body: {historyItem.oldBody}</div>
                                )}
                                <div>
                                  Modified At:{" "}
                                  {new Date(
                                    historyItem.modifiedAt
                                  ).toLocaleString()}
                                </div>
                                <div>Modified By: {historyItem.modifierId}</div>
                              </div>
                            ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminContent;
